/**
 * Shared types and utilities for capability parameter schema handling.
 * Used by NewCapabilityWizard and EditCapabilityDialog.
 */

export type ParameterTypeOption = "string" | "number" | "boolean" | "select" | "object";

export type Parameter = {
  id: string;
  name: string;
  type: ParameterTypeOption;
  description: string;
  required: boolean;
  defaultValue?: string;
  selectOptions?: string[];
  properties?: Parameter[];
  locked?: boolean;
  customizable?: boolean;
};

/**
 * Convert JSON Schema to Parameter array for UI editing
 */
export const schemaToParameters = (schema: Record<string, unknown> | null): Parameter[] => {
  if (!schema) return [];

  const properties = (schema.properties as Record<string, unknown>) || {};
  const required = (schema.required as string[]) || [];

  return Object.entries(properties).map(([name, propSchema]) => {
    const prop = propSchema as Record<string, unknown>;
    let type: ParameterTypeOption = "string";

    if (prop.enum) {
      type = "select";
    } else if (prop.type === "object" || prop.properties) {
      type = "object";
    } else if (prop.type === "number" || prop.type === "integer") {
      type = "number";
    } else if (prop.type === "boolean") {
      type = "boolean";
    }

    const param: Parameter = {
      id: `param-${name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      type,
      description: (prop.description as string) || "",
      required: required.includes(name),
      defaultValue: prop.default !== undefined ? String(prop.default) : undefined,
    };

    if (type === "select" && prop.enum) {
      param.selectOptions = prop.enum as string[];
    }

    if (type === "object" && prop.properties) {
      param.properties = schemaToParameters(prop as Record<string, unknown>);
    }

    return param;
  });
};

/**
 * Convert Parameter array back to JSON Schema for API submission
 */
export const parametersToSchema = (params: Parameter[]): Record<string, unknown> => {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  params.forEach((param) => {
    const propSchema: Record<string, unknown> = {
      type: param.type === "select" ? "string" : param.type,
      description: param.description,
    };

    if (param.required) {
      required.push(param.name);
    }

    if (param.defaultValue !== undefined && param.defaultValue !== "") {
      let defaultValue: unknown = param.defaultValue;
      if (param.type === "boolean") {
        defaultValue = param.defaultValue.toLowerCase() === "true";
      } else if (param.type === "number") {
        defaultValue = parseFloat(param.defaultValue);
      }
      propSchema.default = defaultValue;
    }

    if (param.type === "select" && param.selectOptions) {
      propSchema.enum = param.selectOptions;
    }

    if (param.type === "object" && param.properties) {
      const nested = parametersToSchema(param.properties);
      propSchema.properties = nested.properties;
      if ((nested.required as string[])?.length > 0) {
        propSchema.required = nested.required;
      }
    }

    properties[param.name] = propSchema;
  });

  return {
    type: "object",
    properties,
    required: required.length > 0 ? required : undefined,
  };
};

/**
 * Create a new empty parameter with a unique ID
 */
export const createParameter = (prefix: string = "param"): Parameter => ({
  id: `${prefix}-${Date.now()}`,
  name: "",
  type: "string",
  description: "",
  required: false,
});

/**
 * Add a nested parameter to a parent parameter recursively
 */
export const addNestedParameter = (params: Parameter[], parentId: string): Parameter[] => {
  const newParam = createParameter("nested");
  return params.map((p) => {
    if (p.id === parentId) {
      return { ...p, properties: [...(p.properties || []), newParam] };
    } else if (p.properties) {
      return { ...p, properties: addNestedParameter(p.properties, parentId) };
    }
    return p;
  });
};

/**
 * Remove a parameter by ID recursively
 */
export const removeParameter = (params: Parameter[], id: string): Parameter[] => {
  return params
    .filter((p) => p.id !== id)
    .map((p) => ({
      ...p,
      properties: p.properties ? removeParameter(p.properties, id) : undefined,
    }));
};

/**
 * Update a parameter field by ID recursively
 */
export const updateParameter = (
  params: Parameter[],
  id: string,
  field: keyof Parameter,
  value: unknown
): Parameter[] => {
  return params.map((p) => {
    if (p.id === id) {
      return { ...p, [field]: value };
    } else if (p.properties) {
      return { ...p, properties: updateParameter(p.properties, id, field, value) };
    }
    return p;
  });
};
